/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { formatDateOnly } from '@/utils/date';
import styled from '@emotion/styled';
import {
  Box, Typography, Button, TextField, Grid, Checkbox, FormControlLabel,
  FormHelperText, IconButton, InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { prevStep, setStepVerified } from '../rcSlice';
import { setTempUserInfo, TempUserInfo } from '@/features/habitation/userSlice';
import { registerTempUserRc } from '../rcThunk';
import { useHistory } from 'react-router-dom';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CircleIcon from '@mui/icons-material/Circle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { trackEvent } from '@/utils/trackEvent';
import AddressInput from '../../../components/common/AddressInput';
import { AddressFieldsStyled } from '../../../components/common/AddressFieldsStyled';
import type { Address } from '../../../utils/googlePlace';
import { getMissingAddressFields, hasAnyAddressValue } from '../../../utils/address';

const TextfieldStyled = styled(TextField)`
  & .MuiInputBase-root {
    border-radius: 30px;
  }
  & input {
    padding: 12px 16px;
  }
  & .MuiInputLabel-root[data-shrink="false"] {
    transform: translate(14px, 14px) scale(1);
  }
`;

const PersonalInfoStep = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('rc');
  const { t: ts } = useTranslation('common');
  const history = useHistory();
  const tempUser = useAppSelector(state => state.user);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [address, setAddress] = useState<Address | null>(null);

  // Complétude de l'adresse (STU-28) : le code postal n'est exigé qu'en France,
  // beaucoup de pays n'en ont pas. Le pays, lui, est obligatoire partout car le
  // back le refuse vide (NotBlank dans AddressType).
  const missingAddressFields = getMissingAddressFields(address);
  // « Touché » = l'utilisateur a réellement saisi quelque chose, pas
  // simplement `address !== null` : le lien « Revenir à la recherche » du
  // composant remonte une adresse VIDE, ce qui allumait l'erreur juste après
  // que l'utilisateur ait tout effacé pour recommencer.
  const isAddressTouched = hasAnyAddressValue(address);

  const [formData, setFormData] = useState<TempUserInfo>({
    firstname: tempUser.firstname || '',
    lastname: tempUser.lastname || '',
    email: tempUser.email || '',
    phone: tempUser.phone || '',
    birthdate: tempUser.birthdate || '',
    password: '',
    civility: tempUser.civility || '',
    cguAccepted: tempUser.cguAccepted || false,
  });

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidPhone = (phone: string) => {
    const cleanPhone = phone.replace(/[\s\-.()]/g, '');
    const internationalRegex = /^\+[1-9]\d{0,3}[1-9]\d{3,13}$/;
    const frenchLocalRegex = /^0[1-9](\d{2}){4}$/;
    return internationalRegex.test(cleanPhone) || frenchLocalRegex.test(cleanPhone);
  };

  const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

  const getValidationErrors = () => {
    const errors: Record<string, string> = {};
    if (!formData.firstname) errors.firstname = ts('validation.field.required');
    if (!formData.lastname) errors.lastname = ts('validation.field.required');
    if (!formData.civility) errors.civility = ts('validation.field.required');
    if (!formData.birthdate) errors.birthdate = ts('validation.field.required');
    if (!formData.email) {
      errors.email = ts('validation.field.required');
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Email invalide';
    }
    if (!formData.phone) {
      errors.phone = ts('validation.field.required');
    } else if (!isValidPhone(formData.phone)) {
      errors.phone = 'Téléphone invalide';
    }
    if (!formData.password) {
      errors.password = ts('validation.field.required');
    } else if (!passwordRegex.test(formData.password)) {
      errors.password = ts('validation.field.password.incorrect');
    }
    if (!passwordConfirmation) {
      errors.passwordConfirmation = ts('validation.field.required');
    } else if (formData.password !== passwordConfirmation) {
      errors.passwordConfirmation = ts('validation.field.password.must.match');
    }
    if (missingAddressFields.length > 0) {
      errors.address = ts('validation.field.address.incomplete');
    }
    if (!formData.cguAccepted) {
      errors.cguAccepted = ts('validation.field.required');
    }
    return errors;
  };

  const validationErrors = getValidationErrors();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    if (name === 'passwordConfirmation') {
      if (typeof newValue === 'string') setPasswordConfirmation(newValue);
    } else {
      const updated = { ...formData, [name]: newValue };
      setFormData(updated);
      dispatch(setTempUserInfo({ [name]: newValue }));
    }
  };

  const handleLoginRedirect = () => {
    trackEvent('rc_login_redirect', {
      step: 'personal_info',
      redirect: '/login?redirect=/insurance/rc?step=2',
    });
    history.push('/login?redirect=/insurance/rc?step=2');
  };

  const handleSubmit = async () => {
    trackEvent('rc_step_validated', {
      step: 'personal_info',
      mode: 'register',
      user: {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: formData.phone,
        birthdate: formData.birthdate,
        civility: formData.civility,
      },
    });

    const formattedData = {
      ...formData,
      birthdate: formatDateOnly(formData.birthdate),
    };
    dispatch(setTempUserInfo(formattedData));

    try {
      const resultAction = await dispatch(registerTempUserRc(address));
      if (registerTempUserRc.fulfilled.match(resultAction)) {
        dispatch(setStepVerified(2));
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('step2.title')}
        <Button onClick={handleLoginRedirect} sx={{ color: 'blue' }} size="small" variant="text">
          {t('step2.login_link')}
        </Button>
      </Typography>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>{t('step2.civility')}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {['M', 'Mme'].map((label) => (
              <FormControlLabel
                key={label}
                control={
                  <Checkbox
                    checked={formData.civility === label}
                    onChange={() => {
                      setFormData({ ...formData, civility: label as 'M' | 'Mme' });
                      dispatch(setTempUserInfo({ civility: label as 'M' | 'Mme' }));
                    }}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CircleIcon />}
                  />
                }
                label={label}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled label={t('step2.firstname') + '*'} name="firstname" fullWidth value={formData.firstname} onChange={handleChange} error={!!validationErrors.firstname && touchedFields.firstname} helperText={touchedFields.firstname ? validationErrors.firstname : ''} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled label={t('step2.lastname') + '*'} name="lastname" fullWidth value={formData.lastname} onChange={handleChange} error={!!validationErrors.lastname && touchedFields.lastname} helperText={touchedFields.lastname ? validationErrors.lastname : ''} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled label={t('step2.email') + '*'} name="email" fullWidth value={formData.email} onChange={handleChange} error={!!validationErrors.email && touchedFields.email} helperText={touchedFields.email ? validationErrors.email : ''} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled label={t('step2.phone') + '*'} name="phone" fullWidth value={formData.phone} onChange={handleChange} error={!!validationErrors.phone && touchedFields.phone} helperText={touchedFields.phone ? validationErrors.phone : ''} />
        </Grid>
        <Grid item xs={12}>
          {/* L'habillage « pilule » vit chez l'appelant : `AddressInput` est
              partagé avec la page profil, qui le veut en MUI nu. */}
          <AddressFieldsStyled>
            <AddressInput
              value={address}
              onChange={setAddress}
              error={isAddressTouched && missingAddressFields.length > 0}
            />
          </AddressFieldsStyled>
          {/* Sans ce message, une adresse incomplète (typiquement un pays sans
              code postal) désactivait le bouton "Suivant" sans rien expliquer. */}
          {isAddressTouched && !!validationErrors.address && (
            <FormHelperText error>{validationErrors.address}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled label={t('step2.birthdate') + '*'} name="birthdate" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.birthdate} onChange={handleChange} error={!!validationErrors.birthdate && touchedFields.birthdate} helperText={touchedFields.birthdate ? validationErrors.birthdate : ''} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled
            label={t('step2.password') + '*'}
            name="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={formData.password}
            onChange={handleChange}
            error={!!validationErrors.password && touchedFields.password}
            helperText={touchedFields.password ? validationErrors.password : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextfieldStyled label={t('step2.confirm_password') + '*'} name="passwordConfirmation" type={showPassword ? 'text' : 'password'} fullWidth value={passwordConfirmation} onChange={handleChange} error={!!validationErrors.passwordConfirmation && touchedFields.passwordConfirmation} helperText={touchedFields.passwordConfirmation ? validationErrors.passwordConfirmation : ''} />
        </Grid>
      </Grid>

      <FormControlLabel
        control={<Checkbox icon={<RadioButtonUncheckedIcon />} checkedIcon={<CircleIcon />} checked={formData.cguAccepted} onChange={handleChange} name="cguAccepted" />}
        label={
          <span>
            <a href="/document/Conditions_generales_de_vente.pdf" target="_blank" rel="noreferrer" style={{ color: '#2f6975', textDecoration: 'underline' }}>
              {t('step2.cgu')}
            </a>*
          </span>
        }
        sx={{ mt: 2 }}
      />

      <Typography variant="body1" gutterBottom mb={0} mt={1} textAlign="center">
        {t('step2.already_account')}{' '}
        <Button onClick={handleLoginRedirect} sx={{ color: 'blue' }} size="small" variant="text">
          {t('step2.login_link')}
        </Button>
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={() => dispatch(prevStep())} variant="outlined">{t('step1.back')}</Button>
        <Button onClick={handleSubmit} disabled={Object.keys(validationErrors).length > 0} variant="contained">{t('step1.next')}</Button>
      </Box>
    </Box>
  );
};

export default PersonalInfoStep;
